import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CustomCursor from '@/pages/home/components/CustomCursor';

const services = [
  {
    icon: 'ri-code-s-slash-line',
    title: 'Custom Software Development',
    desc: 'From web applications to enterprise platforms, we build tailored software solutions that solve real business problems. Full-stack development with modern technologies.',
    color: '#FA6E4F',
    bg: 'rgba(250,110,79,0.08)',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'IT Consulting',
    desc: 'Strategic technology advisory to help you make informed decisions. We audit existing systems, recommend optimizations, and guide digital transformation.',
    color: '#8EC9BB',
    bg: 'rgba(142,201,187,0.08)',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Digital Marketing & Advertising',
    desc: 'Data-driven marketing strategies, SEO, PPC campaigns, social media management, and brand positioning to grow your audience and revenue.',
    color: '#F2CF59',
    bg: 'rgba(242,207,89,0.08)',
  },
  {
    icon: 'ri-smartphone-line',
    title: 'Mobile App Development',
    desc: 'Native and cross-platform mobile applications for iOS and Android. User-centric design with smooth performance and scalability.',
    color: '#FB8E7E',
    bg: 'rgba(251,142,126,0.08)',
  },
  {
    icon: 'ri-cloud-line',
    title: 'Cloud Solutions & DevOps',
    desc: 'Cloud migration, infrastructure setup, CI/CD pipelines, and ongoing DevOps support. AWS, Azure, and GCP expertise.',
    color: '#FA6E4F',
    bg: 'rgba(250,110,79,0.08)',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'QA & Testing',
    desc: 'Comprehensive quality assurance including automated testing, manual testing, performance testing, and security audits.',
    color: '#8EC9BB',
    bg: 'rgba(142,201,187,0.08)',
  },
];

function ServiceCard({ service, index, visible }: { service: typeof services[0]; index: number; visible: boolean }) {
  return (
    <div
      className="p-10 rounded-3xl group cursor-default transition-all duration-700"
      style={{
        background: '#161616',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `all 0.8s ease ${index * 100}ms`,
      }}
    >
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-8 transition-transform duration-300 group-hover:scale-110"
        style={{ background: service.bg }}
      >
        <i className={`${service.icon} text-3xl`} style={{ color: service.color }} />
      </div>
      <h3 className="boldonse text-white text-2xl mb-4">{service.title}</h3>
      <p className="text-white/50 text-base leading-relaxed">{service.desc}</p>
    </div>
  );
}

export default function ServicesPage() {
  const [loaded, setLoaded] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 200);
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => { clearTimeout(t); observer.disconnect(); };
  }, []);

  return (
    <main className="w-full overflow-x-hidden bg-[#0d0d0d]">
      <CustomCursor />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-screen flex items-end overflow-hidden pt-24">
        <span
          className="boldonse absolute select-none pointer-events-none"
          style={{
            fontSize: '52vw',
            lineHeight: 1,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -48%)',
            color: 'rgba(255,255,255,0.03)',
            letterSpacing: '-8px',
            whiteSpace: 'nowrap',
          }}
        >
          DEV
        </span>

        <div className="relative z-10 px-10 lg:px-20 pt-40 pb-24 max-w-3xl">
          <div className={`transition-all duration-1000 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <p className="text-[#F8CA9D] text-xs tracking-[0.5em] uppercase mb-10 font-medium">
              &#8212;&nbsp;&nbsp;What we do&nbsp;&nbsp;&#8212;
            </p>
            <h1
              className="boldonse text-white leading-none mb-8"
              style={{ fontSize: 'clamp(64px, 9vw, 130px)', lineHeight: 0.88 }}
            >
              OUR<br />
              <span style={{ color: '#FA6E4F' }}>SERVICES.</span>
            </h1>
          </div>
          <div className={`transition-all duration-1000 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <p className="text-white/60 text-lg leading-relaxed max-w-md">
              End-to-end software development, strategic IT consulting, and powerful digital marketing — everything your business needs to grow in the digital age.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section ref={ref} className="px-10 lg:px-20 py-24">
        <div className={`mb-16 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}`}>
          <p className="text-[#FA6E4F]/60 text-xs tracking-[0.5em] uppercase mb-6">Comprehensive solutions</p>
          <h2 className="boldonse text-white leading-none" style={{ fontSize: 'clamp(44px, 6vw, 90px)' }}>
            HOW WE<br />
            <span style={{ color: '#FA6E4F' }}>HELP</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <ServiceCard key={s.title} service={s} index={i} visible={visible} />
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="px-10 lg:px-20 py-24" style={{ background: '#111' }}>
        <div className="mb-16">
          <p className="text-[#8EC9BB]/60 text-xs tracking-[0.5em] uppercase mb-8">Our workflow</p>
          <h2 className="boldonse text-white leading-none" style={{ fontSize: 'clamp(44px, 6vw, 90px)' }}>
            THE<br />
            <span style={{ color: '#8EC9BB' }}>PROCESS</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {[
            { num: '01', title: 'Discovery', desc: 'Deep dive into your business needs, goals, and constraints.' },
            { num: '02', title: 'Strategy', desc: 'Roadmap, tech stack selection, and project planning.' },
            { num: '03', title: 'Development', desc: 'Agile sprints, daily standups, and transparent progress.' },
            { num: '04', title: 'Delivery', desc: 'Launch, monitoring, and continuous improvement.' },
          ].map((step, i) => (
            <div key={step.num} className="relative">
              <p className="boldonse text-white/10 text-7xl mb-4">{step.num}</p>
              <h4 className="boldonse text-white text-xl mb-3">{step.title}</h4>
              <p className="text-white/40 text-sm leading-relaxed">{step.desc}</p>
              {i < 3 && (
                <div className="hidden md:block absolute top-8 right-0 w-px h-16 bg-white/10" />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-10 lg:px-20 py-32 text-center">
        <p className="text-[#F2CF59] text-xs tracking-[0.5em] uppercase mb-10 font-medium">
          &#8212;&nbsp;&nbsp;Ready to start?&nbsp;&nbsp;&#8212;
        </p>
        <h2
          className="boldonse text-white leading-none mb-10"
          style={{ fontSize: 'clamp(52px, 7.5vw, 110px)', lineHeight: 0.9 }}
        >
          LET&apos;S BUILD<br />
          <span style={{ color: '#FA6E4F' }}>TOGETHER.</span>
        </h2>
        <a
          href="/contact"
          className="inline-block px-10 py-4 rounded-full boldonse text-sm tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          style={{ background: '#FA6E4F', color: '#fff' }}
        >
          GET IN TOUCH
        </a>
      </section>

      <Footer />
    </main>
  );
}